/** IndexedDB draft for unsent general-chat composer text + attachment. */

const DB_NAME = "doculegis-composer-drafts";
const DB_VERSION = 1;
const STORE_NAME = "drafts";

export type ComposerDraftAttachment = {
  name: string;
  mediaType: string;
  /** Raw file bytes stored as a Blob (survives refresh; data URLs are too large for sessionStorage). */
  blob: Blob;
};

export type ComposerDraft = {
  key: string;
  text: string;
  attachment: ComposerDraftAttachment | null;
  updatedAt: number;
};

export type ComposerDraftSnapshot = {
  text: string;
  attachment: {
    name: string;
    mediaType: string;
    dataUrl: string;
  } | null;
};

function openDraftDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error("Failed to open draft database"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
  });
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
    request.onsuccess = () => resolve(request.result);
  });
}

export function composerDraftKey(conversationId: string | undefined | null): string {
  const id = typeof conversationId === "string" && conversationId.trim() ? conversationId.trim() : "new";
  return `supervisor-chat:${id}`;
}

export async function saveComposerDraft(
  key: string,
  draft: { text: string; attachment: ComposerDraftAttachment | null },
): Promise<void> {
  const hasContent = Boolean(draft.text.trim() || draft.attachment);
  if (!hasContent) {
    await clearComposerDraft(key);
    return;
  }
  const db = await openDraftDb();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const record: ComposerDraft = {
      key,
      text: draft.text,
      attachment: draft.attachment,
      updatedAt: Date.now(),
    };
    await idbRequest(store.put(record));
  } finally {
    db.close();
  }
}

export async function loadComposerDraft(key: string): Promise<ComposerDraft | null> {
  const db = await openDraftDb();
  try {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const record = await idbRequest(store.get(key));
    if (!record || typeof record !== "object") return null;
    const draft = record as ComposerDraft;
    if (typeof draft.key !== "string") return null;
    return {
      key: draft.key,
      text: typeof draft.text === "string" ? draft.text : "",
      attachment:
        draft.attachment &&
        typeof draft.attachment.name === "string" &&
        draft.attachment.blob instanceof Blob
          ? {
              name: draft.attachment.name,
              mediaType:
                typeof draft.attachment.mediaType === "string"
                  ? draft.attachment.mediaType
                  : "application/octet-stream",
              blob: draft.attachment.blob,
            }
          : null,
      updatedAt: typeof draft.updatedAt === "number" ? draft.updatedAt : 0,
    };
  } finally {
    db.close();
  }
}

export async function clearComposerDraft(key: string): Promise<void> {
  const db = await openDraftDb();
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await idbRequest(store.delete(key));
  } finally {
    db.close();
  }
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  if (typeof FileReader !== "undefined") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error ?? new Error("Failed to read draft attachment"));
      reader.onload = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        if (!dataUrl) {
          reject(new Error("Empty draft attachment"));
          return;
        }
        resolve(dataUrl);
      };
      reader.readAsDataURL(blob);
    });
  }
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  const type = blob.type || "application/octet-stream";
  return `data:${type};base64,${btoa(binary)}`;
}

export function dataUrlToBlob(dataUrl: string, mediaType?: string): Blob {
  const comma = dataUrl.indexOf(",");
  const meta = comma >= 0 ? dataUrl.slice(0, comma) : "";
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const matched = /data:([^;]+)/.exec(meta);
  const type = mediaType || matched?.[1] || "application/octet-stream";
  return new Blob([bytes], { type });
}

export async function draftToSnapshot(draft: ComposerDraft): Promise<ComposerDraftSnapshot> {
  if (!draft.attachment) {
    return { text: draft.text, attachment: null };
  }
  const dataUrl = await blobToDataUrl(draft.attachment.blob);
  return {
    text: draft.text,
    attachment: {
      name: draft.attachment.name,
      mediaType: draft.attachment.mediaType,
      dataUrl,
    },
  };
}
